import { FloatingRootContext } from '@floating-ui/react'
import {
    IconArrowBackUp,
    IconChevronUp,
    IconCircleCheck,
    IconCircleCheckFilled,
    IconDots,
    IconPencil,
    IconPin,
    IconPinFilled,
    IconPinnedOff,
    IconTrash,
} from '@tabler/icons-react'
import { LexicalEditor as LexicalEditorType } from 'lexical'
import { motion } from 'motion/react'
import { type RefObject, useState } from 'react'

import {
    AstraAvatar,
    AstraButton,
    AstraDropdownItem,
    AstraDropdownMenu,
    AstraIcon,
    AstraParagraph,
    AstraTooltip,
    astraTwMerge,
    day,
    humanizeDate,
} from '~/astra'
import { useStore } from '~/store/store'
import { CommentsThread } from '~/storm'
import { getTimeStampText } from '~/utils/getTimeStampText'
import { getInitials } from '~/utils/user'

import { FloatingHoverMenu, useFloatingHoverMenu } from '../FloatingHoverMenu'
import { ReactionString } from '../RichTextEditing/reactions'
import { AddReactionIcon, CommentMessage } from './CommentMessage'
import { LexicalEditor } from './lexical/LexicalEditor'
import { isLikelyOwner, lexicalEditorToContentPayload, ThreadContentPayload } from './utils'

export type ThreadStatus = 'active' | 'archived' | 'resolved' | 'pinned'

const threadToStatus = (thread: CommentsThread): ThreadStatus => {
    if (thread.resolved) {
        return 'resolved'
    }
    if (thread.archived) {
        return 'archived'
    }
    if (thread.pinned) {
        return 'pinned'
    }
    return 'active'
}

export type OnResolveThread = (thread: CommentsThread) => void

const CommentDivider = () => <span className="mb-3 ml-1 h-4 border-l border-base" />

const ThreadSummary = ({ thread, onOpenReplies }: { thread: CommentsThread; onOpenReplies: () => void }) => {
    if (thread.messages.length < 1) {
        return null
    }

    const lastMessage = thread.messages[thread.messages.length - 1]
    const firstOtherMessage = lastMessage && thread.messages.find(message => message.updated_by !== lastMessage.updated_by)

    return (
        <div className="flex w-full cursor-pointer rounded-lg bg-neutral-subtle-rest p-2 hover:bg-neutral-subtle-hover" onClick={onOpenReplies} tabIndex={0}>
            <div className="flex items-center gap-2">
                <div className="inline-flex">
                    {thread.updated_by && (
                        <span className="z-elevated rounded-full border border-subtle">
                            <AstraAvatar variant="initial" initials={getInitials(thread.updated_by)} size="small" useColorGenerator />
                        </span>
                    )}
                    {firstOtherMessage && firstOtherMessage.updated_by && (
                        <span className="ml-[-11px] rounded-full border border-subtle">
                            <AstraAvatar variant="initial" initials={getInitials(firstOtherMessage.updated_by)} size="small" useColorGenerator />
                        </span>
                    )}
                </div>
                <span className="cursor-pointer text-brand">{thread.messages.length} svar</span>
                {lastMessage && <span className="cursor-pointer text-subtle">Siste svar {getTimeStampText(lastMessage.created_at)}</span>}
            </div>
        </div>
    )
}

const CollapseThread = ({ onCollapse }: { onCollapse: () => void }) => (
    <button
        className="flex cursor-pointer items-center justify-between rounded-lg p-2 hover:bg-neutral-subtle-hover"
        // when clicking the "Skjul svar" while having focus on the input, the blur-event would fire first, causing
        // the input to collapse, and the mouse would "miss" this button due to layout shift.
        // onMouseDown is fired before blur, so we always collapse before layout shift occurs.
        onMouseDown={onCollapse}
        onClick={onCollapse}
    >
        <AstraParagraph weight="regular" className="text-subtle">
            Skjul svar
        </AstraParagraph>
        <AstraIcon icon={IconChevronUp} />
    </button>
)

const ThreadHoverMenu = ({
    thread,
    floatingContext,
    onReplyOpen,
    setFloating,
    onReaction,
    onResolve,
    onPin,
    onDelete,
    onEdit,
    rootPortalRef,
    canEdit,
    ...rest
}: {
    thread: CommentsThread
    floatingContext: FloatingRootContext
    setFloating: (floating: HTMLElement | null) => void
    onReplyOpen?: (open: boolean) => void
    onReaction?: (options: { thread: CommentsThread; replyId?: number; reaction: ReactionString }) => void
    onResolve?: (thread: CommentsThread) => void
    onPin?: (thread: CommentsThread) => void
    onDelete: (thread: CommentsThread) => void
    onEdit: (thread: CommentsThread) => void
    rootPortalRef?: RefObject<HTMLDivElement | null>
    canEdit: boolean
}) => (
    <FloatingHoverMenu floatingContext={floatingContext} setFloating={setFloating} floatingProps={rest} portalRoot={rootPortalRef?.current}>
        {onResolve && (
            <AstraTooltip
                tooltipContent={
                    thread.resolved && thread.resolved_by
                        ? `Markert som løst av ${thread.resolved_by} ${humanizeDate(day(thread.resolved_at ?? ''))}`
                        : 'Merk som løst'
                }
            >
                <AstraButton
                    variant="ghost"
                    icon={thread.resolved ? <AstraIcon icon={IconCircleCheckFilled} /> : <AstraIcon icon={IconCircleCheck} />}
                    aria-label="Merk som løst"
                    onClick={() => {
                        onResolve(thread)
                    }}
                />
            </AstraTooltip>
        )}
        {onPin && (
            <AstraTooltip tooltipContent={thread.pinned ? `Festet av ${thread.pinned_by} ${humanizeDate(day(thread.pinned_at ?? ''))}` : 'Fest kommentar'}>
                <AstraButton
                    variant="ghost"
                    icon={thread.pinned ? <AstraIcon icon={IconPinnedOff} /> : <AstraIcon icon={IconPin} />}
                    aria-label={'Fest kommentar'}
                    onClick={() => {
                        onPin(thread)
                    }}
                />
            </AstraTooltip>
        )}
        {onReplyOpen && (
            <AstraTooltip tooltipContent="Svar">
                <AstraButton variant="ghost" icon={<AstraIcon icon={IconArrowBackUp} />} aria-label="Svar" onClick={() => onReplyOpen(true)} />
            </AstraTooltip>
        )}
        {onReaction && (
            <AstraButton variant="ghost" icon={<AddReactionIcon />} aria-label="Legg til emoji" onClick={() => onReaction({ thread, reaction: 'like' })} />
        )}
        {canEdit && (
            <AstraDropdownMenu
                container={floatingContext.elements.floating}
                noPadding
                placement="bottom-end"
                dropdownContent={
                    <div className="p-1">
                        <AstraDropdownItem
                            option={{ type: 'action', title: 'Rediger', icon: () => <AstraIcon icon={IconPencil} />, action: () => onEdit(thread) }}
                        />
                        <AstraDropdownItem
                            option={{ type: 'action', title: 'Slett', icon: () => <AstraIcon icon={IconTrash} />, action: () => onDelete(thread) }}
                        />
                    </div>
                }
            >
                <AstraButton variant="ghost" icon={<AstraIcon icon={IconDots} />} aria-label="Mer" />
            </AstraDropdownMenu>
        )}
    </FloatingHoverMenu>
)

export type CommentThreadProps = {
    className?: string
    thread: CommentsThread
    onClickDelete?: (id: number) => void
    onReply?: (thread: CommentsThread, content: ThreadContentPayload, editor: LexicalEditorType) => void | Promise<void>
    onReaction?: (options: { thread: CommentsThread; replyId?: number; reaction: ReactionString }) => void
    onResolveThread?: OnResolveThread
    onPinThread?: (thread: CommentsThread) => void
    onDeleteThread: (thread: CommentsThread) => void
    onEditContent?: (thread: CommentsThread, options: ThreadContentPayload & { messageId?: number }) => Promise<void>
    rootPortalRef?: RefObject<HTMLDivElement | null>
    hideMetaInformation?: boolean
}

export const CommentThread = ({
    className,
    thread,
    onReply,
    rootPortalRef,
    onReaction,
    onResolveThread,
    onPinThread,
    onDeleteThread,
    hideMetaInformation,
    onEditContent,
}: CommentThreadProps) => {
    const [isReplyOpen, setIsReplyOpen] = useState(false)
    const [isEditOpen, setIsEditOpen] = useState(false)
    const [editingMessageId, setEditingMessageId] = useState<number | null>(null)
    const user = useStore(state => state.user)

    const { context, getReferenceProps, getFloatingProps, setFloating, setReference } = useFloatingHoverMenu()

    const handleReply = async (editor: LexicalEditorType) => {
        await onReply?.(thread, lexicalEditorToContentPayload(editor), editor)
    }

    const handleEditContent = async (editor: LexicalEditorType, messageId?: number) => {
        if (messageId !== undefined) {
            setEditingMessageId(null)
        } else {
            setIsEditOpen(false)
        }
        try {
            await onEditContent?.(thread, {
                ...lexicalEditorToContentPayload(editor),
                messageId,
            })
        } catch {
            if (messageId !== undefined) {
                setEditingMessageId(messageId)
            } else {
                setIsEditOpen(true)
            }
        }
    }

    const userIsThreadAuthor = isLikelyOwner(thread, user)
    return (
        <motion.li
            data-test="comment-thread"
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 32 }}
            transition={{
                layout: { duration: 0.3, ease: 'easeOut' },
                opacity: { duration: 0.2 },
                y: { duration: 0.2 },
            }}
            className={astraTwMerge(
                className,
                'flex flex-col rounded-xl border border-base bg-surface-base font-hero-regular text-default transition-colors hover:bg-surface-muted',
                context.open && 'bg-surface-muted'
            )}
            ref={setReference}
            tabIndex={0}
            {...getReferenceProps()}
        >
            <ThreadHoverMenu
                thread={thread}
                setFloating={setFloating}
                onReplyOpen={onReply && setIsReplyOpen}
                onReaction={onReaction}
                onResolve={onResolveThread}
                onPin={onPinThread}
                onDelete={onDeleteThread}
                onEdit={() => setIsEditOpen(true)}
                floatingContext={context}
                rootPortalRef={rootPortalRef}
                canEdit={userIsThreadAuthor}
                {...getFloatingProps()}
            />
            {thread.pinned && thread.pinned_by && (
                <div className="flex w-full items-center gap-1 rounded-t-xl border-b border-base bg-neutral-subtle-rest px-3 py-1">
                    <AstraTooltip tooltipContent={humanizeDate(day(thread.pinned_at ?? ''))}>
                        <AstraIcon icon={IconPinFilled} size="small" />
                        <AstraParagraph weight="regular" variant="body-small">
                            Festet av {thread.pinned_by}
                        </AstraParagraph>
                    </AstraTooltip>
                </div>
            )}
            <div className="flex flex-col gap-2 p-3">
                <CommentMessage
                    status={threadToStatus(thread)}
                    reactions={thread.reactions}
                    content={thread.content}
                    onReaction={reaction => onReaction?.({ thread, reaction })}
                    isParent
                    hideMetaInformation={hideMetaInformation}
                    sourcePath={thread.source_path}
                    isEditing={isEditOpen}
                    onEdit={handleEditContent}
                    onCancelEdit={() => setIsEditOpen(false)}
                />
                {isReplyOpen ? (
                    <div className="flex flex-col gap-2">
                        <ol className="flex flex-col gap-2">
                            {thread.messages.map(message => (
                                <li key={message.uuid} className="flex flex-col">
                                    <CommentDivider />
                                    <CommentMessage
                                        reactions={message.reactions}
                                        content={message.content}
                                        onReaction={reaction => onReaction?.({ thread, replyId: message.id, reaction })}
                                        showHoverMenu={onEditContent && isLikelyOwner(message, user)}
                                        isEditing={editingMessageId === message.id}
                                        onEdit={editor => handleEditContent(editor, message.id)}
                                        onCancelEdit={() => setEditingMessageId(null)}
                                        onEditClick={() => setEditingMessageId(message.id)}
                                    />
                                </li>
                            ))}
                        </ol>
                        <CommentDivider />
                        <LexicalEditor
                            key={`${thread.uuid}-${thread.updated_at}`}
                            autoFocus
                            editable
                            onSendMessage={handleReply}
                            userInitials={getInitials(user.display_name ?? '')}
                        />
                        <CollapseThread onCollapse={() => setIsReplyOpen(false)} />
                    </div>
                ) : (
                    <ThreadSummary thread={thread} onOpenReplies={() => setIsReplyOpen(true)} />
                )}
            </div>
        </motion.li>
    )
}
